import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Download, Mail, RefreshCw, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';

interface ProductOrder {
  id: string;
  customer_email: string;
  customer_name: string;
  order_status: string;
  amount_cents: number;
  currency: string;
  download_count: number;
  max_downloads: number;
  token_expires_at: string;
  created_at: string;
  digital_products: {
    name: string;
    sku: string;
  };
}

interface EmailLog {
  id: string;
  email_type: string;
  sent_at: string;
  status: string;
  error_message: string;
  resend_count: number;
}

interface OrderStats {
  totalOrders: number;
  totalRevenue: number;
  completedOrders: number;
  failedDeliveries: number;
  averageDownloads: number;
}

export default function ProductOrdersManagement() {
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);

  useEffect(() => {
    loadOrders();
    loadStats();
  }, []);

  const loadOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('product_orders')
        .select(`
          id,
          customer_email,
          customer_name,
          order_status,
          amount_cents,
          currency,
          download_count,
          max_downloads,
          token_expires_at,
          created_at,
          digital_products (
            name,
            sku
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data: orders, error } = await supabase
        .from('product_orders')
        .select('amount_cents, order_status, download_count')
        .eq('order_status', 'completed');

      if (error) throw error;

      const { data: failedEmails } = await supabase
        .from('product_order_emails')
        .select('id')
        .eq('status', 'failed');

      const totalOrders = orders?.length || 0;
      const totalRevenue = orders?.reduce((sum, o) => sum + o.amount_cents, 0) || 0;
      const completedOrders = orders?.filter(o => o.order_status === 'completed').length || 0;
      const failedDeliveries = failedEmails?.length || 0;
      const averageDownloads = totalOrders > 0
        ? orders.reduce((sum, o) => sum + o.download_count, 0) / totalOrders
        : 0;

      setStats({
        totalOrders,
        totalRevenue: totalRevenue / 100,
        completedOrders,
        failedDeliveries,
        averageDownloads,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadEmailLogs = async (orderId: string) => {
    try {
      const { data, error } = await supabase
        .from('product_order_emails')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEmailLogs(data || []);
      setSelectedOrder(orderId);
    } catch (error) {
      console.error('Error loading email logs:', error);
    }
  };

  const resendEmail = async (order: ProductOrder) => {
    setResendingEmail(order.id);
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/resend-product-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerEmail: order.customer_email,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        alert('Email resent successfully!');
        loadEmailLogs(order.id);
      } else {
        alert(`Failed to resend email: ${result.error}`);
      }
    } catch (error) {
      console.error('Error resending email:', error);
      alert('Failed to resend email');
    } finally {
      setResendingEmail(null);
    }
  };

  const maskEmail = (email: string) => {
    const [name, domain] = email.split('@');
    if (name.length <= 3) return email;
    return `${name.slice(0, 2)}${'*'.repeat(name.length - 2)}@${domain}`;
  };

  const formatCurrency = (cents: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Product Orders</h2>
        <button
          onClick={loadOrders}
          className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-accent transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="text-gray-400 text-sm mb-1">Total Orders</div>
            <div className="text-3xl font-bold text-white">{stats.totalOrders}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="text-gray-400 text-sm mb-1">Total Revenue</div>
            <div className="text-3xl font-bold text-green-400">
              ${stats.totalRevenue.toFixed(2)}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="text-gray-400 text-sm mb-1">Completed</div>
            <div className="text-3xl font-bold text-white">{stats.completedOrders}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="text-gray-400 text-sm mb-1">Failed Emails</div>
            <div className="text-3xl font-bold text-red-400">{stats.failedDeliveries}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="text-gray-400 text-sm mb-1">Avg Downloads</div>
            <div className="text-3xl font-bold text-white">{stats.averageDownloads.toFixed(1)}</div>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Downloads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{order.customer_name || 'N/A'}</div>
                    <div className="text-xs text-gray-400">{maskEmail(order.customer_email)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{order.digital_products.name}</div>
                    <div className="text-xs text-gray-400">{order.digital_products.sku}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {formatCurrency(order.amount_cents, order.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-white">
                        {order.download_count} / {order.max_downloads}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {order.order_status === 'completed' ? (
                      <span className="flex items-center gap-1 text-green-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Completed
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-yellow-400 text-sm">
                        <Clock className="w-4 h-4" />
                        {order.order_status}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadEmailLogs(order.id)}
                        className="text-brand-primary hover:text-brand-accent"
                        title="View Email Logs"
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => resendEmail(order)}
                        disabled={resendingEmail === order.id}
                        className="text-brand-primary hover:text-brand-accent disabled:opacity-50"
                        title="Resend Email"
                      >
                        {resendingEmail === order.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedOrder && emailLogs.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Email Delivery Logs</h3>
          <div className="space-y-3">
            {emailLogs.map((log) => (
              <div key={log.id} className="bg-gray-900 rounded-lg p-4 flex items-start gap-3">
                {log.status === 'sent' ? (
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                ) : log.status === 'failed' ? (
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white capitalize">{log.email_type}</span>
                    <span className="text-xs text-gray-400">
                      {new Date(log.sent_at || log.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400">
                    Status: <span className={`capitalize ${
                      log.status === 'sent' ? 'text-green-400' :
                      log.status === 'failed' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {log.status}
                    </span>
                    {log.resend_count > 0 && (
                      <span className="ml-2">
                        (Retry #{log.resend_count})
                      </span>
                    )}
                  </div>
                  {log.error_message && (
                    <div className="mt-2 text-xs text-red-400 bg-red-900/20 rounded p-2">
                      {log.error_message}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
